/**
 * FacilitateAuthContext — Speaker / Facilitator session management.
 *
 * Security model (completes the reader → admin → speaker trilogy):
 *   - Session is stored in an httpOnly `tcl_speak` cookie set by the server.
 *   - JWT is kept in React state (memory-only); never written to
 *     localStorage or sessionStorage.
 *   - On mount, GET /api/sessions/speakers/me (cookie-authenticated) issues
 *     a fresh token and restores state — same pattern as GET /admin/me.
 *
 * One-time migration: any stale sessionStorage/localStorage entries from the
 * previous implementation are removed on first mount.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FacilitateAuthContext = createContext(null);

export const FacilitateAuthProvider = ({ children }) => {
  const [token, setToken]     = useState(null);
  const [speaker, setSpeaker] = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore session on mount via httpOnly cookie (no storage read).
  useEffect(() => {
    // One-time cleanup of legacy storage keys
    sessionStorage.removeItem('tcl_speak_token');
    sessionStorage.removeItem('tcl_speak_profile');
    localStorage.removeItem('tcl_speaker_token');
    localStorage.removeItem('tcl_speaker');

    const restore = async () => {
      try {
        const res = await fetch(`${API_URL}/api/sessions/speakers/me`, {
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setToken(data.access_token);
          setSpeaker(data.speaker);
        }
      } catch (e) {
        // No active session — stay logged out
      } finally {
        setLoading(false);
      }
    };
    restore();
  }, []);

  /**
   * Call after a successful OTP verification or set-password.
   * The backend has already set the httpOnly cookie; we just store
   * the returned token and profile in memory state.
   */
  const login = (accessToken, speakerData) => {
    setToken(accessToken);
    setSpeaker(speakerData);
  };

  /** Calls the backend logout endpoint to clear the httpOnly cookie. */
  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/sessions/speakers/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (e) {
      console.error('Speaker logout error:', e);
    } finally {
      setToken(null);
      setSpeaker(null);
    }
  };

  return (
    <FacilitateAuthContext.Provider value={{ token, speaker, login, logout, loading }}>
      {children}
    </FacilitateAuthContext.Provider>
  );
};

export const useFacilitateAuth = () => {
  const ctx = useContext(FacilitateAuthContext);
  if (!ctx) throw new Error('useFacilitateAuth must be inside a FacilitateAuthProvider');
  return ctx;
};
